import React, { useState } from 'react';
import { EnvelopeIcon, UserIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';

export default function Contact() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError('');
    console.log('Submitting contact form:', formData);

    // Check if user is authenticated
    if (!user) {
      console.error('User not authenticated');
      setSubmitError('You must be logged in to submit a contact form');
      setIsSubmitting(false);
      return;
    }

    console.log('User authenticated:', user.email);

    try {
      // Make an actual API call to the backend endpoint using the api service
      console.log('Sending request to /contact/submit/');
      const response = await api.post('/contact/submit/', {
        name: formData.fullName,
        email: formData.email,
        message: formData.message,
        subject: 'Contact Form Submission'
      });

      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // With axios, we don't need to check response.ok since it throws errors for non-2xx responses
      console.log('Success response:', response.data);
      setSubmitSuccess(true);
      setFormData({
        fullName: user?.full_name || '',
        email: user?.email || '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form submission error:', error);

      // Handle axios error responses
      if (error.response) {
        // The request was made and the server responded with a status code outside of 2xx range
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);

        if (error.response.status === 401 || error.response.status === 403) {
          setSubmitError('Authentication error: Please log in again to submit the form.');
        } else {
          setSubmitError(error.response.data?.detail || 'Failed to send message. Please try again later.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        setSubmitError('Network error: Could not connect to the server. Please check your connection.');
      } else {
        // Something happened in setting up the request
        setSubmitError(error.message || 'There was an error sending your message. Please try again later.');
      }
    } finally {
      console.log('Contact form submission completed');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
            Have questions about TypoGuard? Need technical support or want to provide feedback?
            Our team is here to help you protect your brand from typosquatting threats.
          </p>
        </div>

        <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden">
          {submitSuccess ? (
            <div className="p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Message Sent Successfully</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Thank you for reaching out! Our team will get back to you as soon as possible.
              </p>
              <button
                type="button"
                onClick={() => setSubmitSuccess(false)}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Full Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text"
                    name="fullName"
                    id="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-2 border ${errors.fullName ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-dark-600 dark:text-white focus:ring-blue-500 focus:border-blue-500'
                      } rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm`}
                    placeholder="Your full name"
                  />
                </div>
                {errors.fullName && (
                  <p className="mt-2 text-sm text-red-600" id="fullName-error">
                    {errors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-2 border ${errors.email ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-dark-600 dark:text-white focus:ring-blue-500 focus:border-blue-500'
                      } rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm`}
                    placeholder="your.email@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Message
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className={`block w-full border p-3 ${errors.message ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-dark-600 dark:text-white focus:ring-blue-500 focus:border-blue-500'
                      } rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm`}
                    placeholder="How can we help you?"
                  />
                </div>
                {errors.message && (
                  <p className="mt-2 text-sm text-red-600" id="message-error">
                    {errors.message}
                  </p>
                )}
              </div>

              {submitError && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                        Error
                      </h3>
                      <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                        <p>{submitError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner className="h-4 w-4 mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Import for the success icon
function CheckIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

// Import for the error icon
function ExclamationCircleIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
